import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GoogleMap, Marker, MarkerClusterer, useJsApiLoader } from '@react-google-maps/api';

const libraries = ['places', 'geometry'];

const PZTMap = ({ reportedLocations: initialReportedLocations = [] }) => {
  // Use the useJsApiLoader hook to load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyBaLMJgUY0VmmLjg-sZzyvhPGQjBcaA5dM",
    language: "hr",
    region: "HR",
    libraries,
  });

  // State management
  const [selectedWaste, setSelectedWaste] = useState(null);
  const [previousMapState, setPreviousMapState] = useState(null);
  const [reportedLocations, setReportedLocations] = useState(initialReportedLocations);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);

  // Map reference
  const mapRef = useRef(null);

  // Constants for Croatia view
  const defaultCenter = { lat: 45.1, lng: 15.2 }; // Better center for Croatia
  const defaultZoom = 8; // Zoom level that shows whole Croatia

  // Initialize with current data on mount
  useEffect(() => {
    if (initialReportedLocations.length === 0) {
      fetchLocationData();
    }
  }, []);

  // Fetch location data from API
  const fetchLocationData = async (startDateParam = '', endDateParam = '') => {
    setIsLoading(true);
    try {
      let url = 'https://pzt.slobodnadomena.hr/public/entries_information';
      
      if (startDateParam && endDateParam) {
        url += `?start_date=${startDateParam}&end_date=${endDateParam}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();

      // Transform the API data to match your existing structure
      const transformedData = data.map(entry => ({
        lat: parseFloat(entry.lat),
        lng: parseFloat(entry.lng),
        category: entry.category?.title || 'Nepoznato',
        reportedDate: entry.created_at,
        place: entry.city?.title || 'Nepoznato mjesto'
      }));
      
      setReportedLocations(transformedData);
    } catch (error) {
      console.error('Error fetching location data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle date filter submission
  const handleDateFilter = () => {
    if (startDate && endDate) {
      fetchLocationData(startDate, endDate);
      setShowDateFilter(false);
      // Close any open popout
      setSelectedWaste(null);
      setPreviousMapState(null);
    }
  };

  // Clear date filter
  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchLocationData(); // Fetch all data
    setShowDateFilter(false);
    setSelectedWaste(null);
    setPreviousMapState(null);
  };

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return "Nepoznato";
    const date = new Date(dateString);
    return date.toLocaleDateString('hr-HR');
  };

  const getReportedIcon = useCallback((location, index) => {
    if (!isLoaded) return null;

    return {
        url: require('../../assets/images/prijavaff7022.svg'),
        scaledSize: new window.google.maps.Size(44, 44),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(20, 39)
      };
  }, [isLoaded]);

  // Handle click on reported waste location
  const handleReportedLocationClick = (location) => {
    // If clicking the same location, close the popout
    if (selectedWaste && selectedWaste.lat === location.lat && selectedWaste.lng === location.lng) {
      closePopout();
      return;
    }

    // Store previous map state if nothing is currently selected
    if (!selectedWaste && mapRef.current) {
      setPreviousMapState({
        center: {
          lat: mapRef.current.getCenter().lat(),
          lng: mapRef.current.getCenter().lng()
        },
        zoom: mapRef.current.getZoom()
      });
    }

    // Mark this pin as clicked and select it
    setSelectedWaste(location);

    // Zoom to the selected location and center it
    if (mapRef.current && isLoaded) {
      mapRef.current.setZoom(14);
      mapRef.current.panTo({ lat: location.lat, lng: location.lng });
    }
  };

  // Close the popout and return to previous view
  const closePopout = () => {
    setSelectedWaste(null);

    // Return to previous map state if it exists
    if (mapRef.current && previousMapState) {
      mapRef.current.setCenter(previousMapState.center);
      mapRef.current.setZoom(previousMapState.zoom);
      setPreviousMapState(null);
    } else if (mapRef.current) {
      // If no previous state, return to default view
      mapRef.current.setCenter(defaultCenter);
      mapRef.current.setZoom(defaultZoom);
    }
  };

  // Filter locations to show only selected one when something is selected
  const visibleLocations = selectedWaste 
    ? reportedLocations.filter(location => 
        location.lat === selectedWaste.lat && location.lng === selectedWaste.lng
      )
    : reportedLocations;

  // Map style options
  const mapOptions = {
    disableDefaultUI: true,
    gestureHandling: "auto",
    scrollwheel: true,
    disableDoubleClickZoom: true,
    styles: [
      {
        "elementType": "geometry",
        "stylers": [{ "color": "#f5f5f5" }]
      },
      {
        "elementType": "labels.icon",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#616161" }]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [{ "color": "#f5f5f5" }]
      },
      {
        "featureType": "administrative",
        "elementType": "geometry",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "administrative.land_parcel",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "administrative.neighborhood",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "poi",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [{ "color": "#ffffff" }]
      },
      {
        "featureType": "road",
        "elementType": "labels",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [{ "color": "#dadada" }]
      },
      {
        "featureType": "road.local",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "transit",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [{ "color": "#c9c9c9" }]
      },
      {
        "featureType": "water",
        "elementType": "labels.text",
        "stylers": [{ "visibility": "off" }]
      }
    ]
  };

  // Handle map load
  const onMapLoad = (map) => {
    mapRef.current = map;
  };

  if (loadError) {
    return <div>Error loading maps: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div>Loading Maps...</div>;
  }

  return (
    <div style={wrapperStyle}>
      {/* Data Summary - moved outside of map */}
      <div style={dataSummaryStyle}>
        <span style={summaryTextStyle}>
          Prikazano: {reportedLocations.length} prijava
          {(startDate && endDate) && (
            <span style={dateRangeTextStyle}>
              {' '}({formatDate(startDate)} - {formatDate(endDate)})
            </span>
          )}
        </span>
      </div>

      <div style={containerStyle}>
        {/* Timeline Filter Button */}
        <div style={filterButtonContainerStyle}>
          <button 
            style={filterToggleButtonStyle}
            onClick={() => setShowDateFilter(!showDateFilter)}
          >
            📅 Filtriraj po datumu
          </button>
        </div>

        {/* Date Filter Panel */}
        {showDateFilter && (
          <div style={dateFilterPanelStyle}>
            <div style={dateFilterHeaderStyle}>
              <h3 style={filterTitleStyle}>Filtriraj po vremenskom periodu</h3>
              <button 
                style={closeFilterButtonStyle} 
                onClick={() => setShowDateFilter(false)}
              >×</button>
            </div>
            <div style={dateFilterContentStyle}>
              <div style={dateInputGroupStyle}>
                <label style={dateInputLabelStyle}>Od datuma:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={dateInputStyle}
                />
              </div>
              <div style={dateInputGroupStyle}>
                <label style={dateInputLabelStyle}>Do datuma:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={dateInputStyle}
                />
              </div>
              <div style={filterActionsStyle}>
                <button
                  style={applyFilterButtonStyle}
                  onClick={handleDateFilter}
                  disabled={!startDate || !endDate}
                >
                  Filtriraj
                </button>
                <button
                  style={clearFilterButtonStyle}
                  onClick={clearDateFilter}
                >
                  Prikaži sve
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div style={loadingOverlayStyle}>
            <div style={loadingContentStyle}>
              <div style={loadingSpinnerStyle}></div>
              <p style={loadingTextStyle}>Učitava podatke...</p>
            </div>
          </div>
        )}

        {/* Popout for selected item */}
        {selectedWaste && (
          <div style={popoutStyle}>
            <div style={popoutHeaderStyle}>
              <h3 style={popoutTitleStyle}>Prijavljeni incident</h3>
              <button style={closeButtonStyle} onClick={closePopout}>×</button>
            </div>
            <div style={popoutContentStyle}>
              <div style={locationContainerStyle}>
                <div style={infoItemStyle}>
                  <p style={metricLabelStyle}>Kategorija</p>
                  <p style={metricValueStyle}>{selectedWaste.category || "Nepoznato"}</p>
                </div>
                <div style={infoItemStyle}>
                  <p style={metricLabelStyle}>Datum prijave</p>
                  <p style={metricValueStyle}>{formatDate(selectedWaste.reportedDate)}</p>
                </div>
              </div>
              <div style={locationContainerStyle}>
                <div style={infoItemStyle}>
                  <p style={metricLabelStyle}>Mjesto</p>
                  <p style={metricValueStyle}>{selectedWaste.place || "Nepoznato"}</p>
                </div>
                <div style={infoItemStyle}>
                  <p style={metricLabelStyle}>Koordinate</p>
                  <p style={metricValueStyle}>({selectedWaste.lat.toFixed(4)}, {selectedWaste.lng.toFixed(4)})</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Google Map */}
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={defaultCenter}
          zoom={defaultZoom}
          options={mapOptions}
          onLoad={onMapLoad}
        >
          {/* Markers for Reported Locations */}
          {visibleLocations.map((location, index) => (
            <Marker
              key={`reported-${index}-${location.lat}-${location.lng}`}
              position={{ lat: location.lat, lng: location.lng }}
              icon={getReportedIcon(location, index)}
              onClick={() => handleReportedLocationClick(location)}
            />
          ))}
        </GoogleMap>
      </div>
    </div>
  );
};

// Styles
const wrapperStyle = {
  width: '100%',
  height: 'calc(100vh - 131px)'
};

const dataSummaryStyle = {
  padding: '14px 20px',
  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
  borderBottom: '1px solid #dee2e6',
  borderLeft: '1px solid #ccc',
  borderRight: '1px solid #ccc',
  borderTop: '1px solid #ccc',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.2s ease'
};

const summaryTextStyle = {
  fontSize: '16px',
  fontFamily: '"medium", sans-serif',
  color: '#2c3e50',
  fontWeight: '500'
};

const dateRangeTextStyle = {
  fontSize: '14px',
  color: '#6c757d',
  fontStyle: 'italic',
  opacity: '0.9'
};

const containerStyle = {
  position: 'relative',
  width: '100%',
  height: 'calc(100% - 48px)',
  border: '1px solid #ccc',
  borderTop: 'none',
  overflow: 'hidden'
};

const filterButtonContainerStyle = {
  position: 'absolute',
  top: '20px',
  left: '20px',
  zIndex: 10
};

const filterToggleButtonStyle = {
  backgroundColor: '#009F4D',
  color: 'white',
  border: 'none',
  padding: '10px 15px',
  borderRadius: '5px',
  cursor: 'pointer',
  fontFamily: '"medium", sans-serif',
  fontSize: '14px',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
};

const dateFilterPanelStyle = {
  position: 'absolute',
  top: '70px',
  left: '20px',
  width: '320px',
  backgroundColor: '#fff',
  borderRadius: '5px',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
  zIndex: 15,
  overflow: 'hidden'
};

const dateFilterHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 15px',
  backgroundColor: '#009F4D',
  color: 'white'
};

const filterTitleStyle = {
  margin: 0,
  fontSize: '16px',
  fontFamily: '"bold", sans-serif'
};

const closeFilterButtonStyle = {
  background: 'none',
  border: 'none',
  color: 'white',
  fontSize: '20px',
  cursor: 'pointer',
  padding: '0'
};

const dateFilterContentStyle = {
  padding: '20px'
};

const dateInputGroupStyle = {
  marginBottom: '15px'
};

const dateInputLabelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontSize: '14px',
  fontFamily: '"medium", sans-serif',
  color: '#333'
};

const dateInputStyle = {
  width: '100%',
  padding: '8px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '14px',
  fontFamily: '"regular", sans-serif'
};

const filterActionsStyle = {
  display: 'flex',
  gap: '10px',
  marginTop: '20px'
};

const applyFilterButtonStyle = {
  flex: 1,
  backgroundColor: '#009F4D',
  color: 'white',
  border: 'none',
  padding: '10px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontFamily: '"medium", sans-serif',
  fontSize: '14px'
};

const clearFilterButtonStyle = {
  flex: 1,
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  padding: '10px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontFamily: '"medium", sans-serif',
  fontSize: '14px'
};

const loadingOverlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 20
};

const loadingContentStyle = {
  textAlign: 'center'
};

const loadingSpinnerStyle = {
  width: '40px',
  height: '40px',
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #009F4D',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  margin: '0 auto 10px'
};

const loadingTextStyle = {
  fontSize: '16px',
  color: '#666',
  fontFamily: '"medium", sans-serif'
};

const popoutStyle = {
  position: 'absolute',
  top: '20px',
  right: '20px',
  height: '28%',
  maxWidth: '350px',
  width: 'calc(100% - 40px)',
  backgroundColor: '#fff',
  borderRadius: '5px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
  zIndex: 10,
  overflow: 'hidden'
};

const popoutHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 15px',
  backgroundColor: '#009F4D',
  color: 'white',
  fontFamily: '"extra-bold", sans-serif'
};

const popoutTitleStyle = {
  margin: 0,
  fontFamily: 'bold',
  fontSize: '1rem'
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  color: 'white',
  fontSize: '24px',
  cursor: 'pointer',
  padding: '0 5px'
};

const popoutContentStyle = {
  padding: '15px',
  height: 'calc(100% - 50px)',
  overflowY: 'auto',
  fontFamily: '"medium", sans-serif'
};

const locationContainerStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px',
  marginBottom: '12px'
};

const infoItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const metricLabelStyle = {
  fontSize: '14px',
  color: '#666666',
  margin: '0',
};

const metricValueStyle = {
  fontSize: '16px',
  fontFamily: 'bold',
  color: '#4d4d4d',
  margin: '0',
};

export default PZTMap;